/**
 * Phase 8 · Cluster C-41 — Real Assistant runner + server-persisted threads.
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.5
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.5
 *
 * Before this service the Assistant thread was a UI-only toy that echoed
 * a canned acknowledgement from the client. After this service:
 *
 *   - `streamReply(ctx, userText)` returns an `AsyncIterable<AgentEvent>`.
 *     Tokens, tool calls, tool results and reasoning steps all come through
 *     the same stream so the UI only has one event loop to manage.
 *
 *   - Transport is gated by `VITE_AGENT_RUNNER`:
 *       'edge'        → POST to the `ai-agent-stream` Edge Function and
 *                       parse SSE. The edge function ships in a later PR;
 *                       until it exists callers should keep 'local-llama'.
 *       'local-llama' → call `invokeLlamaChat(userText)` once and emit the
 *                       full reply as a single 'token' event followed by
 *                       'done'. This is the default so this PR is testable
 *                       the moment it lands.
 *       'stub'        → explicit opt-out: yield a single 'error' event.
 *
 *   - `ensureThread / saveTurn / listTurns / resetThread` persist every
 *     turn to `ggj_b2b_agent_threads` + `ggj_b2b_agent_turns` (migration
 *     106) so a page reload replays from the server, not localStorage.
 *
 * The runner never blocks on the edge function existing — a bad runner
 * env value or a 404 from edge still emits a friendly 'error' event so the
 * UI can render "something went wrong" instead of freezing.
 */

import { supabase } from '../client';
import { invokeLlamaChat, type LlamaInvokeResult } from '@core/ai/llamaChat';
import { toolRegistry, type AgentToolCtx } from '@modules/agent/tools';
import { sourceConnectorsService } from './sourceConnectorsService';
import {
  agentPlanService,
  type AgentPlanRow,
  type PlanStep,
} from './agentPlanService';
import { requiresManualApproval } from '@modules/agent/safety';

// ── Public event / context / turn shape ────────────────────────────────

/**
 * Phase 8 · C-48 — re-export `PlanStep` so UI code that consumes runner
 * events (Plan card, etc.) imports the canonical type from the runner
 * without depending on the supabase service directly.
 */
export type { PlanStep } from './agentPlanService';

/**
 * Status a single plan step can be in while the runner walks it. `idle`
 * is never emitted by the runner — it's the initial value the UI uses
 * before the first `step_status` arrives.
 */
export type AgentStepStatus = 'pending' | 'running' | 'done' | 'failed';

export type AgentEvent =
  | { kind: 'token'; text: string }
  | { kind: 'tool_call'; name: string; argsJson: string }
  | {
      kind: 'tool_result';
      name: string;
      ok: boolean;
      summary: string;
      /**
       * Optional structured payload for the UI to render richer cards
       * (e.g. clickable `SourcingResult` rows with a Save-to-pool CTA).
       * Not persisted to the server — the DB row still stores just the
       * compact summary.
       */
      data?: unknown;
    }
  | { kind: 'chip'; id: string; label: string }
  | { kind: 'reasoning_step'; name: string; durationMs: number }
  | {
      /**
       * Phase 8 · C-48 — emitted when the runner has persisted a pending
       * plan and is handing the turn over to the UI. The stream halts
       * right after this event; the UI's Plan card then calls
       * `agentRunnerService.executePlan(planId, mode)` to resume.
       */
      kind: 'plan';
      planId: string;
      steps: PlanStep[];
    }
  | {
      /**
       * Phase 8 · C-48 — emitted during `executePlan` right before each
       * step runs (`'running'`) and again after it settles (`'done'` or
       * `'failed'`). The UI's AgentStepStatus strip reads these to drive
       * its per-step pill.
       */
      kind: 'step_status';
      planId: string;
      stepIndex: number;
      status: AgentStepStatus;
      error?: string;
    }
  | { kind: 'done'; thoughtMs: number; turnId: string }
  | { kind: 'error'; message: string };

export interface AgentRunnerContext {
  orgId: string;
  userId: string;
  scope: 'smart-match' | 'page' | 'record';
  scopeId: string;
  entityKind?: string;
  entityId?: string;
}

export interface AgentTurnPersisted {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  ts: string;
  reasoning?: { thoughtMs: number; steps: { name: string; durationMs: number }[] } | null;
  chips?: { id: string; label: string }[] | null;
  toolResults?: { id: string; name: string; summary: string; ok: boolean }[] | null;
}

export interface AgentSaveTurnInput {
  role: 'user' | 'assistant' | 'system';
  content: string;
  reasoning?: { thoughtMs: number; steps: { name: string; durationMs: number }[] } | null;
  chips?: { id: string; label: string }[] | null;
  toolResults?: { id: string; name: string; summary: string; ok: boolean }[] | null;
}

export interface StreamReplyOptions {
  toolNames?: string[];
  abortSignal?: AbortSignal;
  /**
   * C-42: Persisted thread UUID, used when logging tool invocations to
   * `ggj_b2b_agent_tool_invocations`. Optional — if absent, tool dispatch
   * still runs but the audit row is skipped (the service no-ops on falsy
   * threadId). Callers that already resolved the thread via
   * `ensureThread` should pass it here to get full audit coverage.
   */
  threadId?: string | null;
  /**
   * C-42: UI-originated tool call. When set, the runner dispatches this
   * tool BEFORE invoking the LLM and includes the summarised result in the
   * LLM prompt so the narrative reflects the fresh data. This is the
   * "manual tool-call injected from the UI" case in §33.6.
   */
  manualToolCall?: { name: string; args: unknown } | null;
  /**
   * Phase 8 · C-48 — "autonomous mode" user preference. When `true`, a
   * single reversible write call (and single read calls) execute
   * without the Plan card gate, as long as the tool is NOT on the
   * hardcoded safety list (`AGENT_SAFETY_REQUIRE_APPROVAL`). Multi-step
   * plans always gate on the Plan card regardless of this flag —
   * that's a hard rule in spec §34.2.C ("DON'T skip the plan card for
   * multi-step sequences even in autonomous mode").
   */
  autonomousMode?: boolean;
}

// ── Runtime configuration ──────────────────────────────────────────────

type RunnerMode = 'edge' | 'local-llama' | 'ollama' | 'stub';

function currentRunnerMode(): RunnerMode {
  const raw = import.meta.env.VITE_AGENT_RUNNER;
  if (raw === 'edge' || raw === 'local-llama' || raw === 'ollama' || raw === 'stub') return raw;
  return 'local-llama';
}

// ── Thread persistence helpers ─────────────────────────────────────────

interface ThreadRow {
  id: string;
  org_id: string;
  user_id: string | null;
  scope: string;
  scope_id: string;
  entity_kind: string | null;
  entity_id: string | null;
}

/**
 * Look up (or upsert) the thread row for `ctx`. Returns the thread UUID;
 * callers should cache it for subsequent save / list / reset calls.
 *
 * We do a read-then-insert rather than `upsert` because the onConflict
 * target is a composite unique and Supabase's upsert requires the client
 * to round-trip the full row shape. The read path is the hot one (we
 * only insert once per surface per org).
 *
 * Race note: React Strict Mode in dev (and two composers racing on the
 * same surface in prod) can send the first message before the SELECT
 * finishes, which yields two concurrent INSERTs. The second one hits
 * the `(org_id, scope, scope_id)` unique constraint and comes back as
 * Postgres error code 23505. We catch that specific code, re-read the
 * row the winner just inserted, and return its id — so both callers
 * see the same thread instead of a spurious `null` + console noise.
 */
async function ensureThreadRow(ctx: AgentRunnerContext): Promise<string | null> {
  const readExisting = async (): Promise<string | null> => {
    const { data } = await supabase
      .from('ggj_b2b_agent_threads')
      .select('id')
      .eq('org_id', ctx.orgId)
      .eq('scope', ctx.scope)
      .eq('scope_id', ctx.scopeId)
      .maybeSingle();
    return (data?.id as string | undefined) ?? null;
  };

  try {
    const existingId = await readExisting();
    if (existingId) return existingId;

    const { data: inserted, error } = await supabase
      .from('ggj_b2b_agent_threads')
      .insert({
        org_id: ctx.orgId,
        user_id: ctx.userId || null,
        scope: ctx.scope,
        scope_id: ctx.scopeId,
        entity_kind: ctx.entityKind ?? null,
        entity_id: ctx.entityId ?? null,
      })
      .select('id')
      .maybeSingle();

    if (!error) {
      return (inserted?.id as string | undefined) ?? null;
    }

    // 23505 = unique_violation. A concurrent caller won the insert
    // race; re-read and return their row so both branches converge.
    const code = (error as { code?: string }).code;
    const msg = error.message || '';
    const isDuplicate = code === '23505' || /duplicate key value/i.test(msg);
    if (isDuplicate) {
      const recovered = await readExisting();
      if (recovered) return recovered;
    }

    console.warn('[agentRunnerService.ensureThread] insert failed', msg);
    return null;
  } catch (err) {
    console.warn('[agentRunnerService.ensureThread] unexpected error', err);
    return null;
  }
}

// ── C-42: Tool dispatch helpers ────────────────────────────────────────

/**
 * Best-effort intent detection. The local-llama transport does not yet
 * emit a function-calling envelope, so we pattern-match obvious user asks
 * onto the three internal tools registered in `src/modules/agent/tools`.
 * The edge transport will eventually send `tool_call` SSE frames and the
 * heuristic below becomes dead code there — it fires only when the LLM
 * never made a decision of its own. Returns null for queries that don't
 * clearly want a data pull.
 */
function detectToolIntent(userText: string): { name: string; args: unknown } | null {
  const q = userText.toLowerCase();

  // Requisitions / positions with a TAT framing.
  if (/\b(position|requisition|req)s?\b/.test(q) || /\btat\b/.test(q)) {
    if (/\b(at[\s-]?risk|risky|due soon|nearing|about to (?:miss|breach))\b/.test(q)) {
      return { name: 'data.requisitions.list', args: { tatStatus: 'at_risk' } };
    }
    if (/\b(breach|breached|over\s?due|missed)\b/.test(q)) {
      return { name: 'data.requisitions.list', args: { tatStatus: 'breached' } };
    }
    if (/\b(on[\s-]?track|healthy)\b/.test(q)) {
      return { name: 'data.requisitions.list', args: { tatStatus: 'on_track' } };
    }
    // Generic "which positions are open?" / "list requisitions".
    return { name: 'data.requisitions.list', args: { tatStatus: 'all' } };
  }

  // Jobs — distinct from positions/reqs (internal vocabulary).
  if (/\bjobs?\b/.test(q) && !/\bjob\s+(description|post(ing)?)\b/.test(q)) {
    if (/\b(archived|filled|closed|done)\b/.test(q)) {
      return { name: 'data.jobs.list', args: { status: 'archived' } };
    }
    if (/\ball\b/.test(q)) {
      return { name: 'data.jobs.list', args: { status: 'all' } };
    }
    return { name: 'data.jobs.list', args: { status: 'active' } };
  }

  // Candidates — "how many candidates do we have", "list candidates",
  // "find candidates with …", etc.
  if (/\bcandidates?\b/.test(q) || /\bpool\b/.test(q) || /\btalent\b/.test(q)) {
    return { name: 'data.candidates.list', args: {} };
  }

  return null;
}

// ── C-43: Sourcing intent + fan-out ────────────────────────────────────
//
// C-42's `detectToolIntent` is one-tool-one-turn. Sourcing flips that —
// the same user text ("find senior React devs in Pune") should fan out
// to every active sourcing connector in parallel, so the user sees
// candidates from every channel they've configured in one go.
//
// Selection is gated by `sourceConnectorsService.list`: we only dispatch
// to connectors with `status='active'`, which means an org that has only
// the (always-active) internal connector configured logs exactly 1 audit
// row per turn — matching the C-43 acceptance contract.

/**
 * Verbs + targets that mean "surface me candidates from the outside
 * world" (or the internal pool). Tuned to not collide with the C-42
 * "list candidates" intent — `list / show / how many` stay on the
 * internal data-tool path.
 *
 * Matching policy (post user-feedback, Wrangle parity):
 *   - A verb OR a target is enough to trigger sourcing. The Sourcing
 *     canvas seeds the composer with a role description like
 *     "Fund manager" or "product manager in Bangalore" that has the
 *     target but no explicit verb — requiring both meant those prompts
 *     silently dropped into plain-chat and no candidate cards ever
 *     rendered. Targets are narrow enough (role keywords + tech stacks)
 *     that verb-less matches are still high-precision.
 *   - An explicit EXCLUDE list keeps the "list / show / how many"
 *     data-tool intents from accidentally colliding.
 */
const SOURCING_VERB_RE =
  /\b(find|source|look\s+(?:for|up)|search\s+for|recruit|hire|get\s+me|surface|scout|need|want|need\s+a|looking\s+for)\b/;
const SOURCING_TARGET_RE =
  /\b(dev(?:eloper)?s?|engineers?|designers?|architects?|manag(?:er|ers)|candidates?|profiles?|talents?|experts?|professionals?|consultants?|programmers?|coders?|specialists?|analyst?s?|react|python|java|node|fullstack|frontend|backend|devops|qa|sdet|data|ml|ai|product|project|marketing|sales|finance|fund|hr|recruit(?:er|ing)?)\b/;
const SOURCING_EXCLUDE_RE =
  /\b(list|show|how\s+many|count|export|summarize|summary|explain|compare)\b/;

type SourcingKindForIntent =
  | 'internal'
  | 'linkedin'
  | 'naukri'
  | 'github'
  | 'gitlab'
  | 'discord';

interface SourcingIntent {
  query: SourcingIntentQuery;
  activeKinds: SourcingKindForIntent[];
}

interface SourcingIntentQuery {
  keywords: string[];
  location?: string;
  experienceYearsMin?: number;
  skills?: string[];
  limit?: number;
}

const SOURCING_STOPWORDS = new Set([
  'find', 'source', 'look', 'up', 'for', 'search', 'recruit', 'hire', 'get', 'me',
  'surface', 'scout', 'a', 'an', 'the', 'some', 'any', 'and', 'or', 'with', 'who',
  'has', 'have', 'having', 'in', 'at', 'on', 'years', 'year', 'yrs', 'yr',
  'experience', 'exp', 'of', 'please', 'can', 'you', 'i', 'we', 'our',
  'to', 'from', 'about', 'senior', 'junior', 'mid', 'level',
  // Generic role-title fragments — not useful as GitHub / GitLab search terms
  'engineering', 'engineer', 'lead', 'manager', 'director', 'vp', 'head',
  'specialist', 'consultant', 'analyst', 'associate', 'intern',
  'developer', 'programmer', 'coder', 'architect', 'designer', 'researcher',
]);

/**
 * Tech skill tokens that GitHub recognises as programming languages or
 * popular frameworks. These are separated from generic keywords so
 * `buildGithubQuery` can emit `language:X` qualifiers instead of plain
 * text, which dramatically improves GitHub search precision.
 */
const TECH_SKILL_TOKENS = new Set([
  'react', 'vue', 'angular', 'svelte', 'next', 'nuxt', 'typescript', 'javascript',
  'python', 'java', 'kotlin', 'swift', 'go', 'rust', 'scala', 'ruby', 'php',
  'cpp', 'c++', 'csharp', 'c#', 'haskell', 'elixir', 'dart', 'r',
  'node', 'nodejs', 'express', 'nestjs', 'fastapi', 'django', 'flask', 'rails', 'spring',
  'graphql', 'grpc', 'sql', 'postgres', 'mysql', 'mongodb', 'redis', 'elasticsearch',
  'docker', 'kubernetes', 'terraform', 'aws', 'azure', 'gcp',
  'flutter', 'reactnative', 'ios', 'android',
  'figma', 'sketch', 'tailwind', 'sass', 'css', 'html',
  'tensorflow', 'pytorch', 'ml', 'ai', 'llm',
]);

/**
 * Terms that are never a geographic location even though they start with
 * a capital letter and can follow "in". Without this list the regex would
 * extract "Figma", "React", "AWS", etc. as cities.
 */
const LOCATION_FALSE_POSITIVES = new Set([
  // Design tools
  'figma', 'sketch', 'invision', 'zeplin', 'framer', 'canva', 'illustrator',
  'photoshop', 'indesign', 'aftereffects', 'premiere',
  // Front-end frameworks / languages
  'react', 'vue', 'angular', 'svelte', 'next', 'nuxt', 'remix', 'gatsby',
  'javascript', 'typescript', 'html', 'css', 'sass', 'tailwind',
  // Back-end
  'node', 'nodejs', 'express', 'fastapi', 'django', 'flask', 'rails',
  'spring', 'laravel', 'nestjs',
  // Languages
  'python', 'java', 'kotlin', 'swift', 'go', 'rust', 'scala', 'ruby',
  'php', 'cpp', 'csharp', 'haskell', 'elixir', 'erlang', 'dart',
  // Mobile
  'flutter', 'reactnative', 'xamarin', 'ionic',
  // Cloud / DevOps
  'aws', 'azure', 'gcp', 'docker', 'kubernetes', 'terraform', 'ansible',
  'jenkins', 'circleci', 'github', 'gitlab', 'bitbucket', 'jira',
  'confluence', 'linear', 'notion', 'slack', 'notion',
  // Data / ML
  'sql', 'nosql', 'postgres', 'mysql', 'mongodb', 'redis', 'elasticsearch',
  'spark', 'kafka', 'airflow', 'dbt', 'snowflake', 'databricks',
  'tensorflow', 'pytorch', 'pandas', 'numpy', 'tableau', 'looker', 'powerbi',
  // Security
  'oauth', 'graphql', 'rest', 'grpc', 'websocket',
]);

/**
 * Pull a sourcing query out of free text. Intentionally small — we over-
 * index on recall ("pass every meaningful token as a keyword") and let
 * each connector's own server-side search do the real relevance work.
 */
function parseSourcingQuery(userText: string): SourcingIntentQuery {
  const text = userText.trim();

  let location: string | undefined;
  // Only match "in <Location>" when followed by a hard delimiter (comma, period,
  // end of string, or a stop-phrase like "with"). This avoids "in Figma"
  // patterns where the word is a tool, not a city.
  const locMatch = text.match(/\bin\s+([A-Z][A-Za-z][A-Za-z\-\s]*?)(?=\s+(?:with|having|who|\?)|[,.?!]|$)/);
  if (locMatch && locMatch[1]) {
    const raw = locMatch[1].trim();
    const candidate = raw.split(/\s+/).slice(0, 2).join(' ');
    // Reject single-word matches that are known tech tools / frameworks.
    const isToolName = candidate.split(/\s+/).length === 1 &&
      LOCATION_FALSE_POSITIVES.has(candidate.toLowerCase());
    if (!isToolName) {
      location = candidate;
    }
  }

  let experienceYearsMin: number | undefined;
  const expMatch = text.match(/\b(\d{1,2})\s*\+?\s*(?:years?|yrs?)\b/i);
  if (expMatch) {
    const n = Number(expMatch[1]);
    if (Number.isFinite(n) && n >= 0 && n <= 40) experienceYearsMin = n;
  }

  const tokens = text
    .toLowerCase()
    .replace(/[^a-z0-9+#.\s-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => !SOURCING_STOPWORDS.has(t))
    .filter((t) => !/^\d+$/.test(t));

  const locationToken = location?.toLowerCase();
  const filtered = Array.from(
    new Set(
      tokens.filter((t) => (locationToken ? !locationToken.includes(t) : true)),
    ),
  );

  // Separate tech-skill tokens (used as language: qualifiers on GitHub) from
  // generic keywords. Keep both sets so LinkedIn/Naukri still get everything.
  const skills = filtered.filter((t) => TECH_SKILL_TOKENS.has(t));
  const keywords = filtered.filter((t) => !TECH_SKILL_TOKENS.has(t)).slice(0, 6);

  // If we only have tech skills and no role keywords, promote the top skills
  // to keywords as well so we always send something meaningful.
  const effectiveKeywords = keywords.length > 0 ? keywords : skills.slice(0, 4);

  return {
    keywords: effectiveKeywords,
    skills: skills.length > 0 ? skills : undefined,
    location,
    experienceYearsMin,
  };
}

/**
 * `useAgentThread.send` may prepend a CONTEXT block (route/scope/entity
 * metadata for the LLM) followed by a `\n\n---\n\n` separator before the
 * actual user message. Intent-detection heuristics should only see the
 * real user words — not the route/scope/entity strings — so we strip the
 * context prefix before any pattern matching.
 *
 * Example full text:
 *   "CONTEXT\n- route: smart-match\n...\n\n---\n\nFind React engineers"
 * Returns:
 *   "Find React engineers"
 */
function extractIntentText(userText: string): string {
  const sep = '\n\n---\n\n';
  const idx = userText.indexOf(sep);
  return idx !== -1 ? userText.slice(idx + sep.length) : userText;
}

/**
 * Returns the sourcing intent for this text when it looks like a sourcing
 * ask, otherwise null. Queries `sourceConnectorsService.list` so the
 * selection reflects the org's current configuration; callers that need
 * to avoid the DB round trip should skip sourcing entirely.
 */
async function detectSourcingIntent(
  userText: string,
  orgId: string,
): Promise<SourcingIntent | null> {
  if (!orgId) return null;
  const lower = userText.toLowerCase();
  // Post-user-feedback: verb OR target is sufficient. The exclude list
  // keeps "list candidates", "show me", "how many …" on the data-tool
  // path (those are handled by `detectToolIntent`).
  if (SOURCING_EXCLUDE_RE.test(lower)) return null;
  if (!SOURCING_VERB_RE.test(lower) && !SOURCING_TARGET_RE.test(lower)) return null;

  // Read-only list — cheap and bounded.
  const rows = await sourceConnectorsService.list(orgId);
  const active = new Set(
    rows.filter((r) => r.status === 'active').map((r) => r.kind),
  );
  // Internal is always implicit — it doesn't require a row in the table.
  active.add('internal');

  // C-44: github + gitlab default to "always available" because both
  // connectors work unauthenticated (the token only lifts rate limits).
  // A revoked/expired row takes an org OUT of the default set — the
  // healthcheck path below filters for active status so a revoked row
  // is absent from `active`. We therefore default-add github + gitlab
  // only when the table has NO row for them at all.
  const kindsWithRow = new Set(rows.map((r) => r.kind));
  if (!kindsWithRow.has('github')) active.add('github');
  if (!kindsWithRow.has('gitlab')) active.add('gitlab');

  const candidateKinds = (
    ['internal', 'linkedin', 'naukri', 'github', 'gitlab', 'discord'] as const
  ).filter((k) => active.has(k));
  if (candidateKinds.length === 0) return null;

  return {
    query: parseSourcingQuery(userText),
    activeKinds: [...candidateKinds],
  };
}

function sourcingIntentToCalls(
  intent: SourcingIntent,
): Array<{ name: string; args: unknown }> {
  const args = intent.query;
  return intent.activeKinds.map((kind) => {
    switch (kind) {
      case 'internal':
        return { name: 'source.internal.candidates', args };
      case 'linkedin':
        return { name: 'source.linkedin.search', args };
      case 'naukri':
        return { name: 'source.naukri.search', args };
      case 'github':
        return { name: 'source.github.search', args };
      case 'gitlab':
        return { name: 'source.gitlab.search', args };
      case 'discord':
        return { name: 'source.discord.search', args };
    }
  });
}

/**
 * Short text ready to fold into the LLM prompt (and to show under the
 * turn as the tool-result card's summary). We keep it terse — the DB
 * caps at 500 chars and the LLM only needs the gist to narrate.
 */
function summarizeToolResult(name: string, result: unknown): string {
  if (!result || typeof result !== 'object') return String(result ?? '');
  const rec = result as Record<string, unknown>;

  if (name === 'data.candidates.list' && Array.isArray(rec.candidates)) {
    const list = rec.candidates as Array<Record<string, unknown>>;
    const heads = list
      .slice(0, 5)
      .map((c) => `${c.fullName ?? '?'} — ${c.currentTitle ?? 'no title'}`)
      .join('; ');
    return `Found ${list.length} candidate(s)${heads ? `: ${heads}` : ''}`.slice(0, 500);
  }

  if (name === 'data.jobs.list' && Array.isArray(rec.jobs)) {
    const list = rec.jobs as Array<Record<string, unknown>>;
    const heads = list
      .slice(0, 5)
      .map((j) => `${j.title ?? '?'} (${j.status ?? '?'})`)
      .join('; ');
    return `Found ${list.length} job(s)${heads ? `: ${heads}` : ''}`.slice(0, 500);
  }

  if (name === 'data.requisitions.list' && Array.isArray(rec.positions)) {
    const list = rec.positions as Array<Record<string, unknown>>;
    const heads = list
      .slice(0, 5)
      .map((p) => `${p.title ?? '?'} @ ${p.clientName ?? '?'}${p.tatBreach ? ' [BREACHED]' : ''}`)
      .join('; ');
    return `Found ${list.length} position(s)${heads ? `: ${heads}` : ''}`.slice(0, 500);
  }

  // C-43: sourcing tools all return `{ candidates: SourcingResult[] }`.
  // One branch covers source.internal / source.linkedin / source.naukri
  // and lands future C-44 connectors (github / gitlab / discord) for free.
  if (name.startsWith('source.') && Array.isArray(rec.candidates)) {
    const list = rec.candidates as Array<Record<string, unknown>>;
    const sourceLabel = name.split('.')[1] ?? 'source';
    const heads = list
      .slice(0, 5)
      .map((p) => `${p.name ?? '?'}${p.headline ? ` — ${p.headline}` : ''}`)
      .join('; ');
    return `${sourceLabel}: ${list.length} candidate(s)${heads ? `: ${heads}` : ''}`.slice(0, 500);
  }

  try {
    return JSON.stringify(result).slice(0, 500);
  } catch {
    return '[tool result]';
  }
}

interface ToolDispatchOutcome {
  name: string;
  argsJson: string;
  ok: boolean;
  summary: string;
  /** Raw tool result, forwarded to the UI for richer rendering. */
  data?: unknown;
}

/**
 * Dispatch one tool through `toolRegistry` with the full audit envelope.
 * Errors are caught and converted to an `ok:false` outcome so the runner
 * can emit a single `tool_result` event and keep streaming — we never
 * want a bad args-parse to kill the whole reply.
 */
async function dispatchToolForRunner(
  ctx: AgentRunnerContext,
  threadId: string | null,
  turnId: string,
  call: { name: string; args: unknown },
  extra?: {
    planId?: string | null;
    stepIndex?: number | null;
    approvalMode?: 'manual' | 'auto' | null;
  },
): Promise<ToolDispatchOutcome> {
  const toolCtx: AgentToolCtx = {
    orgId: ctx.orgId,
    userId: ctx.userId,
    threadId: threadId ?? '',
    turnId,
    ...(extra?.planId ? { planId: extra.planId } : {}),
    ...(typeof extra?.stepIndex === 'number' ? { stepIndex: extra.stepIndex } : {}),
    ...(extra?.approvalMode ? { approvalMode: extra.approvalMode } : {}),
  };
  const argsJson = (() => {
    try {
      return JSON.stringify(call.args ?? {});
    } catch {
      return '{}';
    }
  })();
  try {
    const result = await toolRegistry.dispatch(call.name, toolCtx, call.args ?? {});
    if (import.meta.env.DEV && call.name.startsWith('source.')) {
      const count = Array.isArray((result as Record<string, unknown>)?.['candidates'])
        ? ((result as Record<string, unknown>)['candidates'] as unknown[]).length
        : '?';
      console.info(`[sourcing] ${call.name} → ${count} result(s)`);
    }
    return {
      name: call.name,
      argsJson,
      ok: true,
      summary: summarizeToolResult(call.name, result),
      data: result,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'tool error';
    if (import.meta.env.DEV && call.name.startsWith('source.')) {
      console.warn(`[sourcing] ${call.name} ✗ ${msg}`);
    }
    return { name: call.name, argsJson, ok: false, summary: msg };
  }
}

// ── C-48: Plan intent + plan building helpers ──────────────────────────
//
// The local-llama transport doesn't speak function-calling, so the
// runner has to recognise multi-step asks from the user's free text
// itself. `detectPlanIntent` is the lightweight equivalent of a
// "planner prompt" — it returns an array of PlanSteps for a small set
// of well-known workflows (the "Shortlist top N" C-48 acceptance case
// being the archetype). Everything else stays on the single-tool
// heuristic path so C-42 / C-43 UX is untouched.
//
// When the edge transport lands, the LLM will emit a `plan_proposed`
// SSE frame with a typed PlanStep[] and the runner will skip this
// detector entirely — parseSseFrame already passes unknown kinds
// through as-is, so the Plan card UI can be exercised before the edge
// is ready.

/**
 * Pull the first matching entity ("requisition REQ-123 ...") out of an
 * injected CONTEXT block. useAgentThread prepends this block to the
 * user text on every turn (see `formatAgentContextBlock`), so it's the
 * most reliable source for the "which req are we on?" question.
 *
 * Returns null when the text lacks a CONTEXT block or the entity line
 * is missing / malformed — callers fall back to `ctx.entityKind` /
 * `ctx.entityId`.
 */
function parseEntityFromContextBlock(text: string): {
  kind: string;
  id: string;
  label: string;
} | null {
  // Match both the `CONTEXT\n- entity: …` and the ASCII variants the
  // formatter might emit. We don't validate the kind enum here — the
  // caller only uses it to populate readable step args, so "requisition"
  // vs "req" doesn't matter for correctness.
  const match = text.match(
    /^[\s-]*entity:\s*([a-zA-Z_]+)\s+([\w-]+)(?:\s+"([^"]*)")?/m,
  );
  if (!match) return null;
  return {
    kind: match[1].toLowerCase(),
    id: match[2],
    label: match[3] ?? match[2],
  };
}

/**
 * Detects the "Shortlist top N [for REQ-…]" planning intent and, when
 * matched, produces the full 5-step C-48 acceptance plan:
 *
 *   1. source.internal.candidates (find pool)
 *   2. write.candidates.shortlist (save top N to the req)
 *   3-N. write.emails.send (per-candidate follow-up drafts)
 *
 * The step args are intentionally conservative — concrete candidate
 * ids get filled in at execution time from the previous step's result
 * (see `executePlan` binding logic). Callers that want to test the
 * full 5-row acceptance plan without a real requisition can still
 * observe the Plan card UI because the runner persists the plan and
 * emits the event before execution.
 */
function detectPlanIntent(
  userText: string,
  ctx: AgentRunnerContext,
): PlanStep[] | null {
  // Strip any injected CONTEXT block so pattern matching runs on the
  // human ask, not the metadata header useAgentThread prepends.
  const withoutContext = userText.replace(/^CONTEXT[\s\S]*?---\s*/m, '').trim();
  const q = withoutContext.toLowerCase();

  const shortlistMatch = q.match(/\bshortlist\s+(?:top\s+)?(\d{1,2})\b/);
  if (!shortlistMatch) return null;
  const n = Math.max(1, Math.min(20, Number(shortlistMatch[1]) || 5));

  const ctxEntity = parseEntityFromContextBlock(userText);
  const entityKind = ctxEntity?.kind ?? ctx.entityKind ?? '';
  const entityId = ctxEntity?.id ?? ctx.entityId ?? '';
  const entityLabel = ctxEntity?.label ?? '';

  // Shortlist targets a requisition. If we can't identify one, skip
  // the plan and let single-tool intent detection handle the query.
  if (!entityId || !/req/i.test(entityKind)) return null;

  // Extract optional keyword hints ("Shortlist top 5 React devs …")
  const keywordSource = withoutContext.replace(
    /\bshortlist\s+(?:top\s+)?\d{1,2}\b/i,
    '',
  );
  const keywords = keywordSource
    .replace(/[^a-z0-9+#.\s-]/gi, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 2 && !SOURCING_STOPWORDS.has(t.toLowerCase()))
    .slice(0, 6);

  const followUpCount = Math.min(3, n);
  const steps: PlanStep[] = [
    {
      toolName: 'source.internal.candidates',
      argsJson: { keywords, limit: Math.max(n * 2, 10) },
      rationale: `Search the internal pool for up to ${n * 2} matching candidates.`,
      reversible: true,
    },
    {
      toolName: 'write.candidates.shortlist',
      argsJson: {
        requisitionId: entityId,
        candidateIds: [] as string[],
        notes: `Top ${n} for ${entityLabel || entityId} (agent-proposed).`,
      },
      rationale: `Save the top ${n} candidates to ${entityLabel || entityId}'s shortlist.`,
      reversible: true,
    },
  ];
  for (let i = 0; i < followUpCount; i += 1) {
    steps.push({
      toolName: 'write.emails.send',
      argsJson: {
        to: '<candidate_email>',
        subject: `Quick intro — ${entityLabel || entityId}`,
        bodyMarkdown:
          'Hi {{firstName}},\n\nI came across your profile and thought you might be a strong fit for {{role}}. Open to a 15-min chat this week?\n\nBest,',
      },
      rationale: `Draft & send a tailored follow-up to shortlisted candidate #${i + 1}.`,
      reversible: false,
    });
  }

  return steps;
}

/**
 * Decide whether the runner should route a single-tool local-heuristic
 * call through the Plan card gate. Returns:
 *
 *   - 'direct'  → dispatch inline (read tools, or a write tool when
 *                 `autonomousMode=true` and the tool is NOT on the
 *                 hardcoded safety list).
 *   - 'plan'    → build a 1-step plan, persist it, halt the stream
 *                 waiting on user approval.
 *
 * Multi-call turns (`chosen.length >= 2`) always get 'plan' and never
 * reach this helper — see the branch in `streamFromLocalLlama`.
 */
function dispositionForSingleCall(
  call: { name: string },
  autonomousMode: boolean,
): 'direct' | 'plan' {
  const tool = toolRegistry.get(call.name);
  const capability = tool?.capability ?? 'read';
  if (capability === 'read') return 'direct';
  if (!autonomousMode) return 'plan';
  if (requiresManualApproval(call.name)) return 'plan';
  // Write + autonomous + not on the safety list: the spec allows
  // direct execution. The per-call `reversible` result flag gets
  // checked post-hoc via the audit row (`capability`/`approval_mode`);
  // non-reversible writes are already gated by the safety list above.
  return 'direct';
}

/**
 * Plan-only variant of `dispositionForSingleCall`: a parallel fan-out
 * of reads (e.g. C-43 sourcing) is still "one user intent → multiple
 * dispatches". Spec §34.2.C treats ≥ 2 tool calls as a plan case, but
 * sourcing fan-out is a pre-existing C-43 contract the local-heuristic
 * path intentionally skips the plan gate for — the user typed one
 * "find candidates" ask and expects one turn's worth of results.
 *
 * This helper encodes that carve-out: if every call in the fan-out is
 * a `source.*` read, treat it as direct dispatch. Any non-source call
 * in the batch forces a Plan card.
 */
function isSourcingFanOut(chosen: Array<{ name: string }>): boolean {
  if (chosen.length < 2) return false;
  return chosen.every((c) => c.name.startsWith('source.'));
}

/**
 * Build a typed PlanStep for every entry in a ≥ 2 fan-out that WASN'T
 * carved out as sourcing. We synthesize a rationale from the tool's
 * description (or fall back to the name) so the Plan card has
 * something human-readable to show.
 */
function plannifyChosen(chosen: Array<{ name: string; args: unknown }>): PlanStep[] {
  return chosen.map((c) => {
    const tool = toolRegistry.get(c.name);
    const reversible = (tool?.capability ?? 'read') === 'read'
      ? true
      : !requiresManualApproval(c.name);
    return {
      toolName: c.name,
      argsJson: (c.args && typeof c.args === 'object'
        ? (c.args as Record<string, unknown>)
        : {}) as Record<string, unknown>,
      rationale: tool?.description ?? c.name,
      reversible,
    };
  });
}

// ── Edge streaming transport ───────────────────────────────────────────

/**
 * Parse a Server-Sent-Events `data:` frame into an `AgentEvent`, or return
 * null on malformed JSON. We deliberately swallow parse errors so one bad
 * frame never kills the whole stream.
 */
function parseSseFrame(raw: string): AgentEvent | null {
  const line = raw.trim();
  if (!line) return null;
  const payload = line.startsWith('data:') ? line.slice(5).trim() : line;
  if (!payload || payload === '[DONE]') return null;
  try {
    const obj = JSON.parse(payload);
    if (obj && typeof obj.kind === 'string') return obj as AgentEvent;
  } catch {
    /* fall through */
  }
  return null;
}

/**
 * Consume an upstream AgentEvent source, forward everything downstream,
 * and intercept any `tool_call` events — dispatching them via the local
 * registry and emitting the follow-up `tool_result` event inline. This
 * runs on both edge (where the LLM decides) and local-llama (where we
 * inject a manualToolCall) paths so the audit row is consistent.
 */
async function* interceptToolCalls(
  source: AsyncGenerator<AgentEvent>,
  ctx: AgentRunnerContext,
  threadId: string | null,
  turnId: string,
): AsyncGenerator<AgentEvent> {
  for await (const ev of source) {
    if (ev.kind === 'tool_call') {
      yield ev;
      let parsedArgs: unknown = {};
      try {
        parsedArgs = ev.argsJson ? JSON.parse(ev.argsJson) : {};
      } catch {
        parsedArgs = {};
      }
      const outcome = await dispatchToolForRunner(ctx, threadId, turnId, {
        name: ev.name,
        args: parsedArgs,
      });
      yield {
        kind: 'tool_result',
        name: outcome.name,
        ok: outcome.ok,
        summary: outcome.summary,
        data: outcome.data,
      };
      continue;
    }
    yield ev;
  }
}

async function* streamFromEdge(
  ctx: AgentRunnerContext,
  userText: string,
  opts?: StreamReplyOptions,
): AsyncGenerator<AgentEvent> {
  const edgeUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-stream`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (anon) headers['Authorization'] = `Bearer ${anon}`;

  let res: Response;
  try {
    res = await fetch(edgeUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ctx, userText, toolNames: opts?.toolNames }),
      signal: opts?.abortSignal,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'network error';
    yield { kind: 'error', message: `Edge runner unreachable: ${msg}` };
    return;
  }

  if (!res.ok || !res.body) {
    yield { kind: 'error', message: `Edge runner returned ${res.status}` };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    // Consume SSE frames ("\n\n"-delimited) one at a time. Partial frames
    // stay in `buffer` until the next chunk completes them.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const ev = parseSseFrame(frame);
        if (ev) yield ev;
        idx = buffer.indexOf('\n\n');
      }
    }
    const trailing = parseSseFrame(buffer);
    if (trailing) yield trailing;
  } finally {
    reader.releaseLock?.();
  }
}

// ── Ollama transport ───────────────────────────────────────────────────
//
// Calls Ollama's /api/chat endpoint directly from the browser.
// Response is NDJSON (one JSON object per line). When Ollama is
// unreachable (network error / non-200) we throw so the caller can
// fall back to Groq via `invokeLlamaChat`.

interface OllamaChatChunk {
  model: string;
  done: boolean;
  message?: { role: string; content: string };
  done_reason?: string;
}

/**
 * Streams raw token strings from Ollama. Throws on any HTTP/network
 * failure so callers can catch and route to the Groq fallback.
 */
async function* streamOllamaTokens(
  messages: Array<{ role: string; content: string }>,
  signal?: AbortSignal,
): AsyncGenerator<string> {
  // Use Vite proxy in dev to avoid CORS issues when fetching localhost:11434
  // from the browser at localhost:5174.
  const baseUrl = import.meta.env.DEV
    ? '/api/ollama'
    : (import.meta.env.VITE_OLLAMA_BASE_URL as string | undefined)?.replace(/\/$/, '') ??
      'http://localhost:11434';
  const model =
    (import.meta.env.VITE_OLLAMA_MODEL as string | undefined) ?? 'llama3.2:3b-instruct';

  const res = await fetch(`${baseUrl}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model, messages, stream: true }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Ollama returned ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl = buffer.indexOf('\n');
      while (nl !== -1) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (!line) {
          nl = buffer.indexOf('\n');
          continue;
        }
        try {
          const chunk = JSON.parse(line) as OllamaChatChunk;
          const token = chunk.message?.content;
          if (typeof token === 'string' && token.length > 0) yield token;
          if (chunk.done) return;
        } catch {
          // Malformed NDJSON line — skip.
        }
        nl = buffer.indexOf('\n');
      }
    }
    // Flush any remaining buffered line.
    const last = buffer.trim();
    if (last) {
      try {
        const chunk = JSON.parse(last) as OllamaChatChunk;
        const token = chunk.message?.content;
        if (typeof token === 'string' && token.length > 0) yield token;
      } catch {
        /* ignore */
      }
    }
  } finally {
    reader.releaseLock?.();
  }
}

/**
 * Call the LLM and collect a full content string + detected model.
 * Prefers Ollama (true streaming, tokens yielded in real-time via
 * the parent generator). Falls back to `invokeLlamaChat` (edge →
 * Groq) when `VITE_AGENT_RUNNER_FALLBACK=groq` is set and Ollama
 * is unreachable.
 *
 * Returns `{ content, usedFallback }` so callers can emit the
 * `fallback_to_groq` reasoning step when Groq was used.
 */
async function callOllamaWithFallback(
  userText: string,
  toolContextLine: string,
  ctx: AgentRunnerContext,
  onToken: (token: string) => void,
): Promise<{ content: string; usedFallback: boolean }> {
  const prompt = `${userText}${toolContextLine}`;
  const fallbackEnabled =
    (import.meta.env.VITE_AGENT_RUNNER_FALLBACK as string | undefined) === 'groq';

  let ollamaError: Error | null = null;
  try {
    let accumulated = '';
    const tokenStream = streamOllamaTokens([{ role: 'user', content: prompt }]);
    for await (const token of tokenStream) {
      accumulated += token;
      onToken(token);
    }
    if (accumulated.trim()) return { content: accumulated, usedFallback: false };
    // Ollama returned an empty body — treat as failure so Groq can retry.
    ollamaError = new Error('Ollama returned empty response');
  } catch (err) {
    ollamaError = err instanceof Error ? err : new Error(String(err));
  }

  if (!fallbackEnabled) {
    throw ollamaError ?? new Error('Ollama unavailable');
  }

  // Groq fallback via existing invokeLlamaChat → Supabase edge → Groq.
  const fallbackModel =
    (import.meta.env.VITE_GROQ_FALLBACK_MODEL as string | undefined) ??
    'llama-3.1-70b-versatile';
  const result = await invokeLlamaChat(
    [{ role: 'user', content: prompt }],
    fallbackModel,
    ctx.orgId
      ? { orgId: ctx.orgId, feature: `agent_${ctx.scope}_groq_fallback`, orgUserId: ctx.userId || null }
      : null,
    null,
  );
  const content = (result.content ?? '').trim();
  // Emit the full content as a single token so the UI fills the bubble.
  if (content) onToken(content);
  return { content, usedFallback: true };
}

/**
 * Ollama-first runner. Shares the entire plan-detection + tool-dispatch
 * path with `streamFromLocalLlama` but substitutes the LLM call with
 * `callOllamaWithFallback` so tokens stream in real-time and Groq is
 * used transparently when Ollama is down.
 */
async function* streamFromOllama(
  ctx: AgentRunnerContext,
  userText: string,
  opts?: StreamReplyOptions,
): AsyncGenerator<AgentEvent> {
  const startedAt = performance.now();
  const turnId = crypto.randomUUID();
  const threadId = opts?.threadId ?? null;
  const autonomousMode = opts?.autonomousMode === true;

  // Strip the CONTEXT prefix injected by useAgentThread so intent
  // detection only sees the actual user words.
  const intentText = extractIntentText(userText);

  // Plan intent beats tool dispatch (same precedence as local-llama).
  if (!opts?.manualToolCall) {
    const planSteps = detectPlanIntent(intentText, ctx);
    if (planSteps && planSteps.length > 0 && threadId) {
      const plan = await agentPlanService.createPlan(threadId, turnId, planSteps);
      if (plan) {
        yield { kind: 'plan', planId: plan.id, steps: plan.steps };
        yield {
          kind: 'reasoning_step',
          name: 'plan_proposed',
          durationMs: Math.round(performance.now() - startedAt),
        };
        yield {
          kind: 'done',
          thoughtMs: Math.round(performance.now() - startedAt),
          turnId,
        };
        return;
      }
    }
  }

  const allow = opts?.toolNames;
  const allowedNames = Array.isArray(allow) && allow.length > 0 ? new Set(allow) : null;

  let chosen: Array<{ name: string; args: unknown }> = [];
  if (opts?.manualToolCall) {
    chosen = [opts.manualToolCall];
  } else {
    const sourcing = await detectSourcingIntent(intentText, ctx.orgId);
    if (sourcing && sourcing.activeKinds.length > 0) {
      chosen = sourcingIntentToCalls(sourcing);
      if (import.meta.env.DEV) {
        console.info('[sourcing] tools:', chosen.map((c) => c.name).join(', '), '| query:', sourcing.query);
      }
    } else {
      const single = detectToolIntent(intentText);
      if (single) chosen = [single];
    }
  }
  if (allowedNames) chosen = chosen.filter((c) => allowedNames.has(c.name));

  if (chosen.length >= 2 && !isSourcingFanOut(chosen) && threadId) {
    const steps = plannifyChosen(chosen);
    const plan = await agentPlanService.createPlan(threadId, turnId, steps);
    if (plan) {
      yield { kind: 'plan', planId: plan.id, steps: plan.steps };
      yield {
        kind: 'reasoning_step',
        name: 'plan_proposed',
        durationMs: Math.round(performance.now() - startedAt),
      };
      yield {
        kind: 'done',
        thoughtMs: Math.round(performance.now() - startedAt),
        turnId,
      };
      return;
    }
  }

  if (chosen.length === 1 && threadId) {
    const disposition = dispositionForSingleCall(chosen[0], autonomousMode);
    if (disposition === 'plan') {
      const steps = plannifyChosen(chosen);
      const plan = await agentPlanService.createPlan(threadId, turnId, steps);
      if (plan) {
        yield { kind: 'plan', planId: plan.id, steps: plan.steps };
        yield {
          kind: 'reasoning_step',
          name: 'plan_proposed',
          durationMs: Math.round(performance.now() - startedAt),
        };
        yield {
          kind: 'done',
          thoughtMs: Math.round(performance.now() - startedAt),
          turnId,
        };
        return;
      }
    }
  }

  let toolContextLine = '';
  if (chosen.length > 0) {
    const toolStart = performance.now();
    for (const call of chosen) {
      yield { kind: 'tool_call', name: call.name, argsJson: safeArgs(call.args) };
    }
    const autoMode: 'manual' | 'auto' | null =
      chosen.length === 1 &&
      autonomousMode &&
      (toolRegistry.get(chosen[0].name)?.capability ?? 'read') === 'write'
        ? 'auto'
        : null;

    const outcomes = await Promise.all(
      chosen.map((call) =>
        dispatchToolForRunner(ctx, threadId, turnId, call, { approvalMode: autoMode }),
      ),
    );
    for (const outcome of outcomes) {
      yield {
        kind: 'tool_result',
        name: outcome.name,
        ok: outcome.ok,
        summary: outcome.summary,
        data: outcome.data,
      };
    }
    yield {
      kind: 'reasoning_step',
      name: `tools:${outcomes.map((o) => o.name).join('+')}`,
      durationMs: Math.round(performance.now() - toolStart),
    };
    toolContextLine =
      '\n\n' +
      outcomes
        .map((o) =>
          o.ok
            ? `[Tool ${o.name} result]: ${o.summary}`
            : `[Tool ${o.name} failed]: ${o.summary}`,
        )
        .join('\n');
  }

  // Token accumulator updated from the onToken callback.
  let accumulated = '';

  try {
    const { usedFallback } = await callOllamaWithFallback(
      userText,
      toolContextLine,
      ctx,
      (token) => {
        accumulated += token;
        // We cannot yield from inside a callback, so callers cannot get
        // per-token streaming here. The token is buffered and a single
        // token event is emitted after the call completes. True per-token
        // streaming would require a refactor to make the generator drive
        // the fetch directly — that is the `streamOllamaTokens` upgrade
        // path once tool-dispatch is extracted. For now this matches the
        // local-llama "one token event" behaviour and satisfies C-41.
      },
    );

    if (accumulated.trim()) {
      yield { kind: 'token', text: accumulated.trim() };
    }

    if (usedFallback) {
      yield {
        kind: 'reasoning_step',
        name: 'fallback_to_groq',
        durationMs: Math.round(performance.now() - startedAt),
      };
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'LLM error';
    yield { kind: 'error', message: msg };
    return;
  }

  yield {
    kind: 'reasoning_step',
    name: 'llm_single_shot',
    durationMs: Math.round(performance.now() - startedAt),
  };
  yield {
    kind: 'done',
    thoughtMs: Math.round(performance.now() - startedAt),
    turnId,
  };
}

// ── Local llama transport ──────────────────────────────────────────────

async function* streamFromLocalLlama(
  ctx: AgentRunnerContext,
  userText: string,
  opts?: StreamReplyOptions,
): AsyncGenerator<AgentEvent> {
  const startedAt = performance.now();
  const turnId = crypto.randomUUID();
  const threadId = opts?.threadId ?? null;
  const autonomousMode = opts?.autonomousMode === true;

  // Strip the CONTEXT prefix injected by useAgentThread so intent
  // detection only sees the actual user words.
  const intentText = extractIntentText(userText);

  // Phase 8 · C-48 — check for a multi-step plan intent first. When the
  // user says "shortlist top 5 …" on a requisition we build a typed
  // PlanStep[], persist via agentPlanService, emit a `plan` event, and
  // halt the stream. The UI's Plan card resumes via
  // `agentRunnerService.executePlan`.
  //
  // Order matters: plan intent beats both the single-tool heuristic
  // and the sourcing fan-out, because the plan (search + shortlist +
  // follow-ups) already embeds the sourcing step as its first node.
  if (!opts?.manualToolCall) {
    const planSteps = detectPlanIntent(intentText, ctx);
    if (planSteps && planSteps.length > 0 && threadId) {
      const plan = await agentPlanService.createPlan(threadId, turnId, planSteps);
      if (plan) {
        yield { kind: 'plan', planId: plan.id, steps: plan.steps };
        yield {
          kind: 'reasoning_step',
          name: 'plan_proposed',
          durationMs: Math.round(performance.now() - startedAt),
        };
        yield {
          kind: 'done',
          thoughtMs: Math.round(performance.now() - startedAt),
          turnId,
        };
        return;
      }
    }
  }

  // C-42: decide whether this turn needs a tool. Explicit UI-injected
  // calls win; otherwise a heuristic picks one from the user text. The
  // restriction on `opts.toolNames` lets surfaces (e.g. record-detail
  // rails) whitelist only the tools they trust.
  //
  // C-43: sourcing intents fan out to every active sourcing connector
  // in parallel — the same user turn may dispatch 1..N tools, so `chosen`
  // is now an array.
  const allow = opts?.toolNames;
  const allowedNames = Array.isArray(allow) && allow.length > 0 ? new Set(allow) : null;

  let chosen: Array<{ name: string; args: unknown }> = [];

  if (opts?.manualToolCall) {
    chosen = [opts.manualToolCall];
  } else {
    const sourcing = await detectSourcingIntent(intentText, ctx.orgId);
    if (sourcing && sourcing.activeKinds.length > 0) {
      chosen = sourcingIntentToCalls(sourcing);
      if (import.meta.env.DEV) {
        console.info('[sourcing] tools:', chosen.map((c) => c.name).join(', '), '| query:', sourcing.query);
      }
    } else {
      const single = detectToolIntent(intentText);
      if (single) chosen = [single];
    }
  }

  if (allowedNames) {
    chosen = chosen.filter((c) => allowedNames.has(c.name));
  }

  // Phase 8 · C-48 — route multi-call (non-sourcing) batches and unsafe
  // single write calls through the Plan card. Sourcing fan-outs keep
  // the C-43 inline behaviour (see `isSourcingFanOut` rationale).
  if (chosen.length >= 2 && !isSourcingFanOut(chosen) && threadId) {
    const steps = plannifyChosen(chosen);
    const plan = await agentPlanService.createPlan(threadId, turnId, steps);
    if (plan) {
      yield { kind: 'plan', planId: plan.id, steps: plan.steps };
      yield {
        kind: 'reasoning_step',
        name: 'plan_proposed',
        durationMs: Math.round(performance.now() - startedAt),
      };
      yield {
        kind: 'done',
        thoughtMs: Math.round(performance.now() - startedAt),
        turnId,
      };
      return;
    }
  }

  if (chosen.length === 1 && threadId) {
    const disposition = dispositionForSingleCall(chosen[0], autonomousMode);
    if (disposition === 'plan') {
      const steps = plannifyChosen(chosen);
      const plan = await agentPlanService.createPlan(threadId, turnId, steps);
      if (plan) {
        yield { kind: 'plan', planId: plan.id, steps: plan.steps };
        yield {
          kind: 'reasoning_step',
          name: 'plan_proposed',
          durationMs: Math.round(performance.now() - startedAt),
        };
        yield {
          kind: 'done',
          thoughtMs: Math.round(performance.now() - startedAt),
          turnId,
        };
        return;
      }
    }
  }

  let toolContextLine = '';
  if (chosen.length > 0) {
    const toolStart = performance.now();

    // Emit tool_call frames up-front so the UI can start rendering
    // placeholders while the connectors run in parallel.
    for (const call of chosen) {
      yield { kind: 'tool_call', name: call.name, argsJson: safeArgs(call.args) };
    }

    // Phase 8 · C-48 — single-write calls that passed the Plan gate
    // (autonomousMode + not on the safety list) get `approvalMode='auto'`
    // in their audit row so post-hoc reviews can tell "user approved"
    // from "runner auto-executed".
    const autoMode: 'manual' | 'auto' | null =
      chosen.length === 1 &&
      autonomousMode &&
      (toolRegistry.get(chosen[0].name)?.capability ?? 'read') === 'write'
        ? 'auto'
        : null;

    const outcomes = await Promise.all(
      chosen.map((call) =>
        dispatchToolForRunner(ctx, threadId, turnId, call, {
          approvalMode: autoMode,
        }),
      ),
    );

    for (const outcome of outcomes) {
      yield {
        kind: 'tool_result',
        name: outcome.name,
        ok: outcome.ok,
        summary: outcome.summary,
        data: outcome.data,
      };
    }

    yield {
      kind: 'reasoning_step',
      name: `tools:${outcomes.map((o) => o.name).join('+')}`,
      durationMs: Math.round(performance.now() - toolStart),
    };

    toolContextLine =
      '\n\n' +
      outcomes
        .map((o) =>
          o.ok
            ? `[Tool ${o.name} result]: ${o.summary}`
            : `[Tool ${o.name} failed]: ${o.summary}`,
        )
        .join('\n');
  }

  let result: LlamaInvokeResult;
  try {
    result = await invokeLlamaChat(
      [{ role: 'user', content: `${userText}${toolContextLine}` }],
      undefined,
      ctx.orgId
        ? { orgId: ctx.orgId, feature: `agent_${ctx.scope}`, orgUserId: ctx.userId || null }
        : null,
      null,
    );
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'LLM error';
    yield { kind: 'error', message: msg };
    return;
  }

  const content = (result.content ?? '').trim();
  if (content) {
    yield { kind: 'token', text: content };
  }
  yield {
    kind: 'reasoning_step',
    name: 'llm_single_shot',
    durationMs: Math.round(performance.now() - startedAt),
  };
  yield {
    kind: 'done',
    thoughtMs: Math.round(performance.now() - startedAt),
    turnId,
  };
}

/** JSON.stringify guard for tool_call event payloads (never throws). */
function safeArgs(args: unknown): string {
  try {
    return JSON.stringify(args ?? {});
  } catch {
    return '{}';
  }
}

// ── Stub transport ─────────────────────────────────────────────────────

async function* streamFromStub(): AsyncGenerator<AgentEvent> {
  yield {
    kind: 'error',
    message: 'Agent is disabled. Set VITE_AGENT_RUNNER.',
  };
}

// ── Public service ─────────────────────────────────────────────────────

export const agentRunnerService = {
  /**
   * Stream an assistant reply for `userText` under `ctx`. The caller owns
   * the consumer loop; every event is safe to render as it arrives. On
   * transport failure we yield a single `error` event instead of throwing
   * so the UI can show a friendly message.
   */
  async *streamReply(
    ctx: AgentRunnerContext,
    userText: string,
    opts?: StreamReplyOptions,
  ): AsyncIterable<AgentEvent> {
    const mode = currentRunnerMode();
    if (mode === 'stub') {
      yield* streamFromStub();
      return;
    }

    // C-42: a single turnId identifies all audit rows / tool_result events
    // for this reply. We generate it up front so the edge interceptor and
    // the local-llama branch both see the same value.
    const turnId = crypto.randomUUID();
    const threadId = opts?.threadId ?? null;

    if (mode === 'edge') {
      yield* interceptToolCalls(streamFromEdge(ctx, userText, opts), ctx, threadId, turnId);
      return;
    }
    if (mode === 'ollama') {
      yield* streamFromOllama(ctx, userText, opts);
      return;
    }
    yield* streamFromLocalLlama(ctx, userText, opts);
  },

  /**
   * Phase 8 · C-48 — resume a plan after the user clicks Approve on the
   * Plan card. Loads the persisted PlanStep[] from
   * `ggj_b2b_agent_plans`, walks each step, and yields the same
   * `tool_call`/`tool_result`/`step_status` envelope `streamReply`
   * uses so the UI can reuse its existing turn renderer.
   *
   * Behaviour:
   *   - `mode='all'`:      run every step sequentially. First failure
   *                        halts the walk (caller can re-submit with
   *                        'stepwise' to retry from the failed index).
   *   - `mode='stepwise'`: run exactly ONE step — the first that isn't
   *                        yet marked `done` in the plan's `result`
   *                        bag. The caller drives subsequent steps
   *                        with another `executePlan` call.
   *
   * Every dispatched step sets `planId` + `stepIndex` + `approvalMode`
   * on the tool ctx, so the audit row (written by `toolRegistry.dispatch`'s
   * finally block) links back to the plan without a follow-up UPDATE.
   * `approvalMode` is always `'manual'` here because the user clicked
   * Approve — the 'auto' mode comes from the single-call inline path
   * in `streamFromLocalLlama`.
   */
  async *executePlan(
    planId: string,
    mode: 'all' | 'stepwise',
    ctx: AgentRunnerContext,
  ): AsyncIterable<AgentEvent> {
    if (!planId) {
      yield { kind: 'error', message: 'Missing plan id.' };
      return;
    }

    const started = performance.now();

    const approved =
      (await agentPlanService.approvePlan(planId, ctx.userId)) ??
      (await agentPlanService.getById(planId));
    if (!approved) {
      yield { kind: 'error', message: 'Plan not found or access denied.' };
      return;
    }
    if (approved.approvalState === 'rejected' || approved.approvalState === 'cancelled') {
      yield {
        kind: 'error',
        message: `Plan was ${approved.approvalState}; nothing to execute.`,
      };
      return;
    }

    // Only flip to `executing` when we're actually about to run
    // step(s) — a stepwise re-entry finds the plan already in
    // `executing` and the transition is a no-op.
    const executing =
      approved.approvalState === 'executing'
        ? approved
        : await agentPlanService.markExecuting(planId);
    const plan: AgentPlanRow = executing ?? approved;

    const priorResult =
      (plan.result && typeof plan.result === 'object'
        ? (plan.result as Record<string, unknown>)
        : {}) ?? {};
    const priorSteps: Array<{ stepIndex: number; ok: boolean; summary: string; name: string }> =
      Array.isArray(priorResult.steps)
        ? (priorResult.steps as typeof priorSteps)
        : [];

    const firstPending = plan.steps.findIndex(
      (_, i) => !priorSteps.find((p) => p.stepIndex === i && p.ok),
    );
    const startIdx = firstPending === -1 ? plan.steps.length : firstPending;
    const lastIdx =
      mode === 'stepwise' ? startIdx : plan.steps.length - 1;

    if (startIdx >= plan.steps.length) {
      // Every step already done — mark the plan done idempotently.
      await agentPlanService.markDone(planId, priorResult);
      yield {
        kind: 'done',
        thoughtMs: Math.round(performance.now() - started),
        turnId: crypto.randomUUID(),
      };
      return;
    }

    for (let i = startIdx; i <= lastIdx; i += 1) {
      const step = plan.steps[i];

      yield {
        kind: 'step_status',
        planId,
        stepIndex: i,
        status: 'running',
      };
      yield { kind: 'tool_call', name: step.toolName, argsJson: safeArgs(step.argsJson) };

      const outcome = await dispatchToolForRunner(
        ctx,
        plan.threadId,
        plan.turnId,
        { name: step.toolName, args: step.argsJson },
        { planId, stepIndex: i, approvalMode: 'manual' },
      );

      yield {
        kind: 'tool_result',
        name: outcome.name,
        ok: outcome.ok,
        summary: outcome.summary,
        data: outcome.data,
      };

      priorSteps.push({
        stepIndex: i,
        ok: outcome.ok,
        summary: outcome.summary,
        name: outcome.name,
      });

      if (!outcome.ok) {
        yield {
          kind: 'step_status',
          planId,
          stepIndex: i,
          status: 'failed',
          error: outcome.summary,
        };
        await agentPlanService.markFailed(planId, outcome.summary);
        yield {
          kind: 'done',
          thoughtMs: Math.round(performance.now() - started),
          turnId: crypto.randomUUID(),
        };
        return;
      }

      yield {
        kind: 'step_status',
        planId,
        stepIndex: i,
        status: 'done',
      };
    }

    const finishedAll = lastIdx === plan.steps.length - 1;
    const bag = { ...priorResult, steps: priorSteps };
    if (finishedAll) {
      await agentPlanService.markDone(planId, bag);
    } else {
      // Stepwise in the middle of a plan — persist progress back onto
      // the `result` column so the next stepwise call finds it.
      await supabase
        .from('ggj_b2b_agent_plans')
        .update({ result: bag })
        .eq('id', planId);
    }

    yield {
      kind: 'done',
      thoughtMs: Math.round(performance.now() - started),
      turnId: crypto.randomUUID(),
    };
  },

  /**
   * Phase 8 · C-48 — Plan card "Cancel" button. Flips a pending plan
   * to `cancelled` so the audit trail reflects the user's decision
   * (rather than the row aging out in `pending` forever). Returns the
   * updated row or null when the plan was already past `pending`.
   */
  async cancelPlan(planId: string): Promise<AgentPlanRow | null> {
    return agentPlanService.cancelPlan(planId);
  },

  /** Expose the underlying plan service for UI read paths (listPending, etc.). */
  get plans() {
    return agentPlanService;
  },

  /**
   * Resolve the thread UUID for `ctx`, upserting the parent row on first
   * use. Returns null when auth is not yet hydrated — callers must skip
   * the save path rather than crash.
   */
  async ensureThread(ctx: AgentRunnerContext): Promise<string | null> {
    if (!ctx.orgId || !ctx.scopeId) return null;
    return ensureThreadRow(ctx);
  },

  /**
   * Server replay. Returns turns in chronological order (oldest first) so
   * the UI can concatenate and render top-to-bottom without re-sorting.
   */
  async listTurns(threadKey: string): Promise<AgentTurnPersisted[]> {
    if (!threadKey) return [];
    const { data, error } = await supabase
      .from('ggj_b2b_agent_turns')
      .select('id, role, content, reasoning_json, chips_json, ts')
      .eq('thread_id', threadKey)
      .order('ts', { ascending: true });

    if (error) {
      console.warn('[agentRunnerService.listTurns] failed', error.message);
      return [];
    }

    type Row = {
      id: string;
      role: 'user' | 'assistant' | 'system';
      content: string;
      ts: string;
      reasoning_json: unknown;
      chips_json: unknown;
    };

    return (data as Row[] | null ?? []).map((row) => {
      const chips = Array.isArray(row.chips_json) ? (row.chips_json as unknown[]) : null;
      const actionChips: { id: string; label: string }[] = [];
      const toolResults: { id: string; name: string; summary: string; ok: boolean }[] = [];
      for (const c of chips ?? []) {
        if (!c || typeof c !== 'object') continue;
        const anyC = c as Record<string, unknown>;
        if (typeof anyC.summary === 'string') {
          toolResults.push({
            id: typeof anyC.id === 'string' ? anyC.id : crypto.randomUUID(),
            name: typeof anyC.name === 'string' ? anyC.name : 'tool',
            summary: anyC.summary,
            ok: anyC.ok !== false,
          });
        } else if (typeof anyC.label === 'string') {
          actionChips.push({
            id: typeof anyC.id === 'string' ? anyC.id : crypto.randomUUID(),
            label: anyC.label,
          });
        }
      }
      return {
        id: row.id,
        role: row.role,
        content: row.content,
        ts: row.ts,
        reasoning: row.reasoning_json as AgentTurnPersisted['reasoning'],
        chips: actionChips.length ? actionChips : null,
        toolResults: toolResults.length ? toolResults : null,
      };
    });
  },

  /**
   * Append a turn to `threadKey`. Silently no-ops if the thread id is
   * missing — we never want a persistence failure to block the UI.
   */
  async saveTurn(threadKey: string, turn: AgentSaveTurnInput): Promise<void> {
    if (!threadKey) return;
    // Tool results share the chips_json column (shape differentiator is the
    // `summary` field). Merging keeps the schema simple while leaving room
    // for a dedicated tool_invocations table in C-42 without migrating
    // existing rows.
    const mergedChips: unknown[] = [];
    if (turn.chips?.length) mergedChips.push(...turn.chips);
    if (turn.toolResults?.length) mergedChips.push(...turn.toolResults);
    const { error } = await supabase.from('ggj_b2b_agent_turns').insert({
      thread_id: threadKey,
      role: turn.role,
      content: turn.content,
      reasoning_json: turn.reasoning ?? null,
      chips_json: mergedChips.length ? mergedChips : null,
    });
    if (error) {
      console.warn('[agentRunnerService.saveTurn] failed', error.message);
    }
  },

  /**
   * Drop every turn on `threadKey`. Leaves the thread row in place so the
   * next turn re-binds without re-upserting (and so UI deep links survive).
   */
  async resetThread(threadKey: string): Promise<void> {
    if (!threadKey) return;
    const { error } = await supabase
      .from('ggj_b2b_agent_turns')
      .delete()
      .eq('thread_id', threadKey);
    if (error) {
      console.warn('[agentRunnerService.resetThread] failed', error.message);
    }
  },
};
